import React, { useEffect } from "react";
import RegisterPage from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiGet } from "../lib/api";

const TYPE = {
  Equipment: { bg: "#DBEAFE", color: "#1E40AF" },
  Daily: { bg: "#DCFCE7", color: "#166534" },
  Weekly: { bg: "#FEF3C7", color: "#854D0E" },
  "Monthly Audit": { bg: "#EDE9FE", color: "#5B21B6" },
};

const RISK = {
  Low: { bg: "#DCFCE7", color: "#166534" },
  Medium: { bg: "#FEF3C7", color: "#854D0E" },
  High: { bg: "#FFE4E6", color: "#9F1239" },
  Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
};

function normalizeStatus(status) {
  return String(status || "Active").toLowerCase() === "active" ? "Active" : "Inactive";
}

function mapTemplate(row, items = []) {
  return {
    id: `TPL-${String(row.id).padStart(3, "0")}`,
    title: row.title || "",
    type: row.checklist_type || "",
    equipmentCategory: row.equipment_category || "Not Applicable",
    frequency: row.frequency || "",
    site: row.site_name || "All Sites",
    items: items.map((item) => item.question).filter(Boolean).join("; "),
    requiredRemarks: items.some((item) => String(item.required_remarks || "").toLowerCase() === "yes") ? "Yes" : "No",
    riskLevel: row.risk_level || "Medium",
    correctiveActionRequired: row.corrective_action_required || "No",
    status: normalizeStatus(row.status),
    backendId: row.id,
    siteId: row.site_id,
    rawItems: items,
  };
}

export default function ChecklistTemplates() {
  const { data, setData, siteNames } = useHSE();
  const sites = ["All Sites", ...siteNames];

  useEffect(() => {
    let cancelled = false;

    async function loadChecklistTemplates() {
      try {
        const templates = await apiGet("/api/checklist-templates");
        if (cancelled || !Array.isArray(templates)) return;

        const itemPairs = await Promise.all(
          templates.map(async (template) => {
            try {
              const items = await apiGet(`/api/checklist-templates/${template.id}/items`);
              return [template.id, Array.isArray(items) ? items : []];
            } catch (error) {
              console.warn(`Checklist items unavailable for template ${template.id}`, error);
              return [template.id, []];
            }
          })
        );

        const itemsByTemplate = Object.fromEntries(itemPairs);
        const mapped = templates.map((template) => mapTemplate(template, itemsByTemplate[template.id] || []));

        if (!mapped.length) return;

        setData((prev) => ({
          ...prev,
          checklistTemplates: mapped,
        }));
      } catch (error) {
        console.warn("Checklist Templates API unavailable, using local fallback data.", error);
      }
    }

    loadChecklistTemplates();

    return () => {
      cancelled = true;
    };
  }, [setData]);

  return (
    <RegisterPage
      title="Checklist Templates"
      subtitle="Admin-managed checklist template register prepared for future D1 checklist tables"
      storeKey="checklistTemplates"
      idPrefix="TPL"
      columns={[
        { key: "id", label: "Template ID" },
        { key: "title", label: "Checklist Title" },
        { key: "type", label: "Checklist Type" },
        { key: "equipmentCategory", label: "Equipment Category" },
        { key: "frequency", label: "Frequency" },
        { key: "site", label: "Site / Project" },
        { key: "items", label: "Checklist Questions / Items" },
        { key: "requiredRemarks", label: "Required Remarks" },
        { key: "riskLevel", label: "Risk Level" },
        { key: "correctiveActionRequired", label: "CA Required" },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ type: TYPE, riskLevel: RISK }}
      formFields={[
        { key: "id", label: "Template ID" },
        { key: "title", label: "Checklist Title" },
        { key: "type", label: "Checklist Type", type: "select", options: data.settings.checklistTypes || ["Equipment", "Daily", "Weekly", "Monthly Audit"] },
        { key: "equipmentCategory", label: "Equipment Category", type: "select", options: ["Not Applicable", "Site Wide", ...(data.settings.equipmentCategories || [])] },
        { key: "frequency", label: "Frequency", type: "select", options: ["Daily", "Weekly", "Monthly"] },
        { key: "site", label: "Site / Project", type: "select", options: sites },
        { key: "items", label: "Checklist Questions / Items", type: "textarea", full: true },
        { key: "requiredRemarks", label: "Required Remarks", type: "select", options: ["Yes", "No"] },
        { key: "riskLevel", label: "Risk Level", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "correctiveActionRequired", label: "Corrective Action Required", type: "select", options: ["Yes", "No"] },
        { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
      ]}
    />
  );
}
